import { useMemo } from "react";
import { useGetLocale, useTranslate } from "@refinedev/core";

import { formatDate } from "../../i18n/format";
import type { ComponentStatus } from "./dierComponenten";
import { statusVanEntingen, statusVanGeldigheid } from "./dierStatus";
import type { PensionStand } from "../pensionKlantTypes";

/**
 * De Gezondheidsring: de eerste tegelvorm uit het ontwerp van 30-09-2026.
 *
 * <p>Vijf onderdelen in één beeld -- vaccinaties, ontworming, ontvlooiing, medicatie en gewicht. De vier
 * die een geldigheid kennen staan als segment in de ring; het gewicht staat in het midden, want dat is
 * een getal en geen toestand.
 *
 * <p><b>Waarom een ring en niet vier bolletjes.</b> Een ring beantwoordt in één oogopslag de vraag "mag
 * dit dier het pension in", zonder dat je vier regels moet lezen. Het segment met het ergste nieuws
 * bepaalt de kleur van het geheel: half gedekt is geen toestand waar iemand een dier op aanneemt.
 *
 * <p><b>Een onbekend segment blijft grijs en telt niet mee.</b> Nooit ontwormd is iets anders dan te
 * laat ontwormd. Een dier waarvan niets bekend is groen kleuren leest als een controle die gedaan is;
 * rood kleuren leest als een overschreden termijn die er niet is. Grijs zegt wat het is: we weten het
 * niet.
 */

/** De vier segmenten, in de volgorde van de matrix. */
const SEGMENTEN = ["vaccinaties", "ontworming", "ontvlooiing", "medicatie"] as const;

export type Dichtheid = "compact" | "normaal" | "uitgebreid";

export const RING_KLEUR: Record<ComponentStatus | "onbekend", string> = {
  // De themakleur en niet een vast groen: een eigen thema zet geen "success", en dan stond er groen in
  // een bruin scherm. Aandacht en verlopen blijven amber en rood -- dat zijn waarschuwingen.
  inOrde: "var(--fawkes-color-primary)",
  aandacht: "var(--fawkes-color-warning)",
  verlopen: "var(--fawkes-color-danger)",
  onbekend: "var(--fawkes-color-border)",
};

/** De ernst, om het ergste segment te vinden. Onbekend telt niet mee; zie de klasse-uitleg. */
const ERNST: Record<ComponentStatus | "onbekend", number> = {
  onbekend: 0,
  inOrde: 1,
  aandacht: 2,
  verlopen: 3,
};

export interface RingSegment {
  sleutel: string;
  status: ComponentStatus | "onbekend";
  label: string;
}

/**
 * De status per segment uit de stand die het dierscherm toch al ophaalt.
 *
 * <p>Medicatie krijgt geen status maar wel een segment: lopende medicatie is een gegeven en niet iets
 * dat goed of fout staat. Het segment is dan blauwgrijs en zegt alleen dát er iets loopt -- een kleur
 * eraan hangen zou beweren dat medicatie een toestand is, en dat is aan de dierenarts.
 */
export function ringSegmenten(
  stand: PensionStand | null,
  vandaag: Date,
  t: (sleutel: string) => string,
  /**
   * Welke van de vier segmenten meedoen; leeg betekent alle vier.
   *
   * <p>Nodig sinds de ring ook voor zelfgemaakte tegels gebruikt wordt. Zou hij altijd alle vier tonen,
   * dan tekent een tegel met gewicht en voeding erin toch een ring over entingen en ontworming -- en dan
   * beweert de tegel iets wat er niet in staat.
   */
  alleen?: string[],
): RingSegment[] {
  const mee = alleen ? SEGMENTEN.filter((s) => alleen.includes(s)) : SEGMENTEN;
  return mee.map((sleutel) => {
    const status: ComponentStatus | "onbekend" =
      sleutel === "vaccinaties"
        ? statusVanEntingen(stand?.entingenVerlopen, stand?.entingGeldigTot, vandaag) ?? "onbekend"
        : sleutel === "ontworming"
          ? statusVanGeldigheid(stand?.ontwormingGeldigTot, vandaag) ?? "onbekend"
          : sleutel === "ontvlooiing"
            ? statusVanGeldigheid(stand?.ontvlooiingGeldigTot, vandaag) ?? "onbekend"
            : "onbekend";
    return { sleutel, status, label: t(`dierTegel.component.${sleutel}`) };
  });
}

/** De slechtste status van de ring; {@code onbekend} als er van niets iets bekend is. */
export function ringStatus(segmenten: RingSegment[]): ComponentStatus | "onbekend" {
  return segmenten.reduce<ComponentStatus | "onbekend">(
    (erg, s) => (ERNST[s.status] > ERNST[erg] ? s.status : erg),
    "onbekend",
  );
}

/**
 * De ring zelf.
 *
 * <p>Vier gelijke bogen met een gat ertussen, getekend als {@code stroke-dasharray} op één cirkel per
 * segment. Dat is minder code dan vier paden en blijft rond bij elke maat, want een cirkel schaalt met
 * zijn {@code viewBox} mee -- een tegel die van compact naar breed gaat hoeft niets opnieuw te rekenen.
 */
export function Gezondheidsring({
  stand,
  gewichtKg,
  gewichtDatum,
  dichtheid = "normaal",
  vandaag = new Date(),
  alleen,
  foto,
  onOpenen,
  kinderen,
}: {
  stand: PensionStand | null;
  gewichtKg?: number | null;
  gewichtDatum?: string | null;
  dichtheid?: Dichtheid;
  vandaag?: Date;
  /** Welke segmenten meedoen; leeg betekent alle vier. Zie {@link ringSegmenten}. */
  alleen?: string[];
  /**
   * De foto van het dier, als rondje in het midden.
   *
   * <p>Dan staat er in de ring wie het is in plaats van wat hij weegt. Op een dashboard met meerdere
   * dieren scheelt dat lezen: een foto herken je sneller dan een naam. Het gewicht verdwijnt niet --
   * het staat als onderdeel in de lijst ernaast, waar het ook zijn eigen knop heeft.
   */
  foto?: string | null;
  /** Klik op de achtergrond opent het volledige dieroverzicht; leeg laat de tegel stil. */
  onOpenen?: () => void;
  /** De onderdelen onder de ring. Bij compact alleen als er geen ring te tekenen valt. */
  kinderen?: React.ReactNode;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const segmenten = useMemo(() => ringSegmenten(stand, vandaag, t, alleen), [stand, vandaag, t, alleen]);
  const geheel = ringStatus(segmenten);

  // Vier gelijke bogen op een cirkel met straal 42: omtrek 2πr, per boog een kwart min een gat.
  const straal = 42;
  const omtrek = 2 * Math.PI * straal;
  const gat = 6;
  // Over het aantal segmenten dat meedoet: met twee segmenten zijn het halve cirkels en niet twee
  // kwarten met een gat van een halve ring ertussen.
  const deel = omtrek / Math.max(1, segmenten.length);
  const boog = deel - gat;

  const kg = gewichtKg != null ? new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(gewichtKg) : null;

  return (
    <div className="dt-ring" data-dichtheid={dichtheid} data-toon={geheel}>
      <div
        className="dt-ring__vlak"
        // De achtergrond opent het dieroverzicht; de knoppen van de onderdelen doen hun eigen ding en
        // houden de klik bij zich.
        onClick={onOpenen}
        role={onOpenen ? "button" : undefined}
        tabIndex={onOpenen ? 0 : undefined}
        onKeyDown={onOpenen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenen(); } } : undefined}
        aria-label={onOpenen ? t("dierTegel.ring.openen") : undefined}
      >
        {/* Een ring van één boog is geen ring: hij ziet eruit als een bijna gesloten cirkel in een
            kleur die over het geheel gaat, terwijl er maar één ding in gemeten wordt. Bij minder dan
            twee segmenten blijft alleen het midden over -- de foto of het gewicht -- en staan de
            gekozen onderdelen eronder, waar ze toch al stonden. */}
        <svg className="dt-ring__svg" viewBox="0 0 100 100" role="img" data-leeg={segmenten.length < 2 || undefined}
          aria-label={t(`dierTegel.ring.samenvatting.${geheel}`)}>
          {(segmenten.length < 2 ? [] : segmenten).map((s, i) => (
            <circle
              key={s.sleutel}
              cx="50"
              cy="50"
              r={straal}
              fill="none"
              stroke={RING_KLEUR[s.status]}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={`${boog} ${omtrek - boog}`}
              // Kwart slag terug zodat het eerste segment bovenaan begint in plaats van rechts.
              strokeDashoffset={-(deel * i) + omtrek / 4}
              transform="rotate(-90 50 50)"
            >
              <title>{`${s.label}: ${t(`dierTegel.ring.status.${s.status}`)}`}</title>
            </circle>
          ))}
        </svg>
        <div className="dt-ring__midden">
          {foto ? (
            <img className="dt-ring__foto" src={foto} alt="" />
          ) : kg != null ? (
            <>
              <strong className="dt-ring__gewicht">{t("dierTegel.gewicht.kg", { args: [kg.replace(".", ",")] })}</strong>
              {gewichtDatum && dichtheid !== "compact" && (
                <span className="dt-ring__datum">{formatDate(gewichtDatum.slice(0, 10), locale)}</span>
              )}
            </>
          ) : (
            <span className="dt-ring__leeg">{t("dierTegel.ring.nietGewogen")}</span>
          )}
        </div>
      </div>
      {/* Compact toont alleen de ring: kernwaarde, status en verder niets. Dat is de regel uit het
          ontwerp voor vijf of meer tegels naast elkaar. */}
      {/* Compact toont normaal alleen de ring; die vertelt dan het verhaal in één oogopslag. Maar als er
          geen ring te tekenen valt -- minder dan twee segmenten -- zou de tegel leeg zijn, en dan zijn
          de onderdelen juist het enige wat er te melden is. */}
      {(dichtheid !== "compact" || segmenten.length < 2) && kinderen
        ? <div className="dt-ring__onderdelen">{kinderen}</div>
        : null}
    </div>
  );
}
